import { createEvent, createStore } from 'effector';

export type SceneLoadingStatus = 'idle' | 'loading' | 'preview' | 'ready' | 'error';

export type SplatLoadingPhase = 'idle' | 'fetching' | 'initializing' | 'ready';

export type SceneLoadingState = {
  status: SceneLoadingStatus;
  loadedBytes: number;
  totalBytes: number | null;
  splatLoadedBytes: number;
  splatTotalBytes: number | null;
  splatPhase: SplatLoadingPhase;
};

export type SceneLoadFailureStage = 'preview' | 'splat';

export type SceneLoadFailurePayload = {
  stage: SceneLoadFailureStage;
  error?: unknown;
};

export const sceneLoadingStarted = createEvent();
export const sceneLoadingProgressed = createEvent<{ loadedBytes: number; totalBytes: number | null }>();
export const scenePreviewLoadingFinished = createEvent();
export const sceneLoadingFinished = createEvent();
export const sceneLoadingFailed = createEvent<SceneLoadFailurePayload>();
export const splatLoadingProgressed = createEvent<{ loadedBytes: number; totalBytes: number | null }>();
export const splatFetchCompleted = createEvent();
export const splatInitCompleted = createEvent();
export const sceneLoadingReset = createEvent();

const initialState: SceneLoadingState = {
  status: 'idle',
  loadedBytes: 0,
  totalBytes: null,
  splatLoadedBytes: 0,
  splatTotalBytes: null,
  splatPhase: 'idle',
};

/**
 * Wraps a success-side reducer so it early-returns when the store is in
 * sticky-error state — a late preview/splat success must not overwrite the
 * error overlay (CLAUDE.md §1). Only `sceneLoadingReset` clears the error.
 */
const guardError =
  <P>(reducer: (state: SceneLoadingState, payload: P) => SceneLoadingState) =>
  (state: SceneLoadingState, payload: P): SceneLoadingState =>
    state.status === 'error' ? state : reducer(state, payload);

export const $sceneLoadingState = createStore<SceneLoadingState>(initialState)
  .reset(sceneLoadingReset)
  .on(sceneLoadingStarted, () => ({
    status: 'loading',
    loadedBytes: 0,
    totalBytes: null,
    splatLoadedBytes: 0,
    splatTotalBytes: null,
    splatPhase: 'idle',
  }))
  .on(sceneLoadingProgressed, (state, payload) => ({
    ...state,
    loadedBytes: payload.loadedBytes,
    totalBytes: payload.totalBytes,
  }))
  .on(scenePreviewLoadingFinished, guardError((state) => ({ ...state, status: 'preview' })))
  .on(
    splatLoadingProgressed,
    guardError<{ loadedBytes: number; totalBytes: number | null }>((state, payload) => ({
      ...state,
      splatLoadedBytes: payload.loadedBytes,
      splatTotalBytes: payload.totalBytes,
      splatPhase: state.splatPhase === 'idle' ? 'fetching' : state.splatPhase,
    })),
  )
  .on(splatFetchCompleted, guardError((state) => ({ ...state, splatPhase: 'initializing' })))
  .on(splatInitCompleted, guardError((state) => ({ ...state, splatPhase: 'ready' })))
  .on(
    sceneLoadingFinished,
    guardError((state) => ({
      ...state,
      status: 'ready',
      loadedBytes: state.totalBytes ?? state.loadedBytes,
    })),
  )
  .on(sceneLoadingFailed, (state) => ({
    ...state,
    status: 'error',
  }));
